import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { IoEyeOffOutline } from 'react-icons/io5';
import adminApi from '../../services/adminApi';
import { apiErrorMessage } from '../../utils/apiError';
import AdminDataTable from '../../components/admin/AdminDataTable';
import './AdminLeaderboardsPage.css';

const CATEGORIES = [
  { id: 'chapters', label: 'Chapters' },
  { id: 'verses', label: 'Verses' },
  { id: 'words', label: 'Words' },
  { id: 'weighted', label: 'Coverage' },
  { id: 'family_chapters', label: 'Families' }
];

const TIMEFRAMES = [
  { id: 'all', label: 'This season' },
  { id: 'month', label: 'This month' },
  { id: 'week', label: 'This week' }
];

const AGE_GROUPS = [
  { id: '', label: 'All ages' },
  { id: 'kids', label: 'Kids (0-12)' },
  { id: 'teens', label: 'Teens (13-17)' },
  { id: 'adults', label: 'Adults (18-54)' },
  { id: 'seniors', label: '55+' }
];

// Text and an icon, not color alone, so the marker reads in any theme
const OptedOutBadge = ({ family = false }) => (
  <span
    className="leaderboard-optout"
    title={family
      ? 'No member of this family has competitor mode on. The public board shows "Anonymous Family".'
      : 'Competitor mode is off. The public board shows "Anonymous Reader".'}
  >
    <IoEyeOffOutline aria-hidden="true" />
    Competitor mode off
  </span>
);

/**
 * One board's table. Exported for tests. Columns are chosen from
 * board.category, the category the rows were loaded for.
 */
export const LeaderboardTable = ({ board, loading = false, error = null, onRetry = null }) => {
  const { entries, available } = board;
  const isFamily = board.category === 'family_chapters';
  const metricKey = isFamily ? 'chapters' : board.category;
  const metricLabel = CATEGORIES.find((item) => item.id === board.category)?.label || 'Value';
  const optedOutCount = entries.filter((entry) => entry.competitorModeDisabled).length;

  const readerColumns = [
    { key: 'rank', header: '#', className: 'leaderboard-rank', render: (entry) => entry.rank },
    {
      key: 'name',
      header: 'Reader',
      render: (entry) => (
        <div className="leaderboard-name">
          <strong>{entry.reader.name}</strong>
          {entry.competitorModeDisabled && <OptedOutBadge />}
        </div>
      )
    },
    {
      key: 'account',
      header: 'Account',
      render: (entry) => (entry.account ? (
        <Link to={`/admin/readers?userId=${entry.reader.userId}`} className="leaderboard-account">
          {entry.account.email}
        </Link>
      ) : '—')
    },
    {
      key: 'value',
      header: metricLabel,
      className: 'leaderboard-value',
      render: (entry) => (entry.value ?? entry[metricKey] ?? 0).toLocaleString()
    }
  ];

  const familyColumns = [
    { key: 'rank', header: '#', className: 'leaderboard-rank', render: (entry) => entry.rank },
    {
      key: 'name',
      header: 'Family',
      render: (entry) => (
        <div className="leaderboard-name">
          <strong>{entry.family.name}</strong>
          {entry.competitorModeDisabled && <OptedOutBadge family />}
        </div>
      )
    },
    { key: 'members', header: 'Readers', render: (entry) => entry.family.memberCount },
    {
      key: 'value',
      header: 'Chapters',
      className: 'leaderboard-value',
      render: (entry) => (entry.chapters ?? 0).toLocaleString()
    }
  ];

  return (
    <AdminDataTable
      title={`Top ${entries.length}${optedOutCount ? ` · ${optedOutCount} with competitor mode off` : ''}`}
      rows={entries}
      getRowId={(entry) => (isFamily ? entry.family.id : entry.reader.id)}
      getRowLabel={(entry) => (isFamily ? entry.family.name : entry.reader.name)}
      columns={isFamily ? familyColumns : readerColumns}
      loading={loading}
      error={error}
      onRetry={onRetry}
      emptyTitle={available ? 'No reading yet' : 'Not available yet'}
      emptyMessage={available
        ? 'Nobody has logged reading for this board and timeframe.'
        : 'Word counts for every chapter have not been loaded, so this board is hidden on the public site too.'}
    />
  );
};

const AdminLeaderboardsPage = () => {
  const [category, setCategory] = useState('chapters');
  const [timeframe, setTimeframe] = useState('all');
  const [ageGroup, setAgeGroup] = useState('');
  // The rows travel with the category they were loaded for. Family and reader
  // entries have different shapes, so columns must follow the loaded rows, not
  // the tab that was just clicked and has not loaded yet.
  const [board, setBoard] = useState({ category: 'chapters', entries: [], available: true });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const latestRequest = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++latestRequest.current;
    try {
      setLoading(true);
      const response = await adminApi.getLeaderboard({
        category,
        timeframe,
        ageGroup: category === 'family_chapters' ? '' : ageGroup,
        limit: 100
      });
      if (requestId !== latestRequest.current) return; // a newer tab or filter won
      setBoard({
        category,
        entries: response.data.leaderboard || [],
        available: response.data.available !== false
      });
      setError(null);
    } catch (err) {
      if (requestId !== latestRequest.current) return;
      setError(apiErrorMessage(err, 'Failed to load the leaderboard.'));
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [category, timeframe, ageGroup]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="admin-leaderboards-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Leaderboards</h1>
        <p className="admin-page-description">
          The public leaderboards with real names. Readers marked &ldquo;Competitor mode off&rdquo;
          appear as anonymous to everyone else, including on the public board when you view it.
        </p>
      </div>

      <div className="admin-card leaderboard-controls">
        <div className="leaderboard-tabs" role="tablist" aria-label="Leaderboard">
          {CATEGORIES.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={category === item.id}
              className={category === item.id ? 'is-active' : ''}
              onClick={() => setCategory(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="leaderboard-filters">
          <label>
            <span>Timeframe</span>
            <select value={timeframe} onChange={(event) => setTimeframe(event.target.value)}>
              {TIMEFRAMES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </label>
          {category !== 'family_chapters' && (
            <label>
              <span>Age group</span>
              <select value={ageGroup} onChange={(event) => setAgeGroup(event.target.value)}>
                {AGE_GROUPS.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
              </select>
            </label>
          )}
        </div>
      </div>

      <LeaderboardTable board={board} loading={loading} error={error} onRetry={load} />
    </div>
  );
};

export default AdminLeaderboardsPage;
